'use client';

import { X, ChevronDown } from 'lucide-react';
import { useState } from 'react';

const countryCodes = [
  { code: '+1', label: 'USA' },
  { code: '+44', label: 'UK' },
  { code: '+49', label: 'Germany' },
  { code: '+33', label: 'France' },
  { code: '+81', label: 'Japan' },
  { code: '+86', label: 'China' },
  { code: '+91', label: 'India' },
  { code: '+7', label: 'Russia' },
  { code: '+55', label: 'Brazil' },
  { code: '+61', label: 'Australia' },
];

export default function BookDemoModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState({
    name: '',
    website: '',
    email: '',
    phone: '',
    info: '',
    countryCode: '+1',
    privacyAccepted: false,
  });

  const [isCodeDropdownOpen, setIsCodeDropdownOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const submissionData = {
        name: formData.name,
        website: formData.website,
        email: formData.email,
        phone: formData.countryCode + ' ' + formData.phone,
        info: formData.info,
      };

      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send message');
      }

      // Track Google Ads conversions (both conversion IDs)
      if (typeof window !== 'undefined' && window.gtag) {
        // First conversion ID
        window.gtag('event', 'conversion', {
          send_to: 'AW-17468017599/2udrCLLvuKkbEL-Xs4lB',
          value: 1.0,
          currency: 'USD',
        });

        // Second conversion ID
        window.gtag('event', 'conversion', {
          send_to: 'AW-17468017599/22aOCI29xKkbEL-Xs4lB',
          value: 1.0,
          currency: 'USD',
        });
      }

      // Clear form and close modal
      setFormData({
        name: '',
        website: '',
        email: '',
        phone: '',
        info: '',
        countryCode: '+1',
        privacyAccepted: false,
      });

      alert("Thank you! We'll reach out to you within 24 hours.");
      onClose();
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start sm:items-center justify-center z-50">
      <div className="bg-white w-full min-h-screen sm:min-h-0 sm:max-w-xl sm:rounded-3xl sm:max-h-[95vh] sm:overflow-y-auto relative">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        <form onSubmit={handleSubmit} className="p-6 sm:p-8">
          <h2 className="text-2xl font-medium text-gray-900 mb-6 pt-2 text-center sm:text-left">
            Contact us
          </h2>
          <div className="space-y-6">
            <div>
              <label className="block text-base font-medium mb-1.5">Name</label>
              <input
                type="text"
                placeholder="John Smith"
                className="w-full p-3 border border-gray-200 rounded-xl text-gray-600"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>

            <div>
              <label className="block text-base font-medium mb-1.5">Website</label>
              <input
                type="text"
                placeholder="www.company.com"
                className="w-full p-3 border border-gray-200 rounded-xl text-gray-600"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                required
              />
            </div>

            <div>
              <label className="block text-base font-medium mb-1.5">Email</label>
              <input
                type="email"
                placeholder="johnsmith@company.com"
                className="w-full p-3 border border-gray-200 rounded-xl text-gray-600"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>

            <div>
              <label className="block text-base font-medium mb-1.5">Phone</label>
              <div className="flex gap-2">
                <div className="relative">
                  <button
                    type="button"
                    className="flex items-center gap-1 border border-gray-200 rounded-xl px-3 h-[50px] hover:border-gray-300"
                    onClick={() => setIsCodeDropdownOpen(!isCodeDropdownOpen)}
                  >
                    <span className="text-gray-600">{formData.countryCode}</span>
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  </button>

                  {isCodeDropdownOpen && (
                    <div className="absolute top-full left-0 mt-1 w-40 bg-white border border-gray-200 rounded-xl shadow-lg py-2 z-50">
                      {countryCodes.map((country) => (
                        <button
                          key={country.code}
                          type="button"
                          className="w-full px-4 py-2 text-left hover:bg-gray-50 text-gray-600"
                          onClick={() => {
                            setFormData({ ...formData, countryCode: country.code });
                            setIsCodeDropdownOpen(false);
                          }}
                        >
                          {country.label} ({country.code})
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <input
                  type="tel"
                  placeholder="(206) 451-2559"
                  className="flex-1 p-3 border border-gray-200 rounded-xl text-gray-600"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-base font-medium mb-1.5">Info</label>
              <textarea
                placeholder="Tell us a bit more about your goals or anything you feel might be important"
                className="w-full p-3 border border-gray-200 rounded-xl text-gray-600 min-h-[100px]"
                value={formData.info}
                onChange={(e) => setFormData({ ...formData, info: e.target.value })}
              />
            </div>

            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="privacy"
                className="mt-1"
                checked={formData.privacyAccepted}
                onChange={(e) => setFormData({ ...formData, privacyAccepted: e.target.checked })}
                required
              />
              <label htmlFor="privacy" className="text-sm text-gray-600">
                I confirm that I&apos;ve read and agree to the{' '}
                <a href="#" className="text-lime-600 hover:underline">
                  Privacy Policy
                </a>{' '}
                and consent to my personal data being processed. I understand and agree that LenSync
                may send me emails occasionally
              </label>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#01A7FF] text-white text-center py-4 rounded-xl text-lg font-medium hover:bg-[#0196e6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Send'}
            </button>

            <p className="text-center text-gray-600">We&apos;ll reach out to you within 24 hours</p>
          </div>
        </form>
      </div>
    </div>
  );
}
